import React, { useState, useEffect } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Importing icons for password visibility toggle
import axios from "axios";
import config from "../config";
import { useLocation } from "react-router-dom";
import "./PasswordReset.css";
import { PasswordResetReturn } from "../Interfaces/AccountUsers";
import { encodeQueryParameter } from "../CommonFunctions";

interface PasswordResetProps {
  token: string;
}

const PasswordReset: React.FC<PasswordResetProps> = ({ token }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // For toggling password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // For toggling confirm password visibility
  const [loading, setLoading] = useState(false);
  const [formMessage, setFormMessage] = useState({
    message: "",
    isError: false,
  });
  const [isValidToken, setIsValidToken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenResponse, setTokenResponse] =
    useState<null | PasswordResetReturn>(null);

  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });

  const validatePasswordStrength = (password: string) => {
    const criteria = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[\W_]/.test(password),
    };
    setPasswordCriteria(criteria);
    return Object.values(criteria).every(Boolean);
  };

  useEffect(() => {
    const checkToken = async () => {
      try {
        // Make the API call to check token validity
        const response = await axios.get(
          `${config.API_BASE_URL}password_reset_request/${token}/`
        );

        if (response.status === 200) {
          setIsValidToken(true);
          setTokenResponse(response.data);
        } else {
          setIsValidToken(false);
        }
      } catch (err) {
        setError("Invalid or expired token. Please request a new reset link.");
        setIsValidToken(false); // Token is invalid in case of error
      } finally {
        setLoading(false); // Set loading to false once the API call is complete
      }
    };

    checkToken(); // Call the function to verify the token
    document.title = "Reset Password"; // Set the page title
  }, [token]); // Dependency on the `token`, will re-run if token changes

  // TODO do we have anything to check that the new password != old password? Often times I think websites will
  // have something like that where its like hey, u used this before (but only show on submit
  // so ur not like brute force figuring out somebody's password lol)
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormMessage({ message: "", isError: false });

    if (password !== confirmPassword) {
      setFormMessage({ message: "Passwords do not match!", isError: true });
      return;
    }

    if (!validatePasswordStrength(password)) {
      setFormMessage({
        message: "Password does not meet the required criteria.",
        isError: true,
      });
      return;
    }

    if (tokenResponse?.user.email.toLowerCase() !== email) {
      setFormMessage({
        message: "Email provided does not match with our records.",
        isError: true,
      });
      return;
    }

    setLoading(true);
    const encodedPassword = encodeQueryParameter({ password: password });

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}password_reset_request/reset`,
        JSON.stringify({
          email: email,
          password: encodedPassword,
          token: token,
        }),
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        setFormMessage({
          message: "Password reset successful!",
          isError: false,
        });
        setEmail("");
        setPassword("");
        setConfirmPassword("");
      } else {
        const errorData = response.data;
        setFormMessage({
          message: errorData.detail || "Password reset failed.",
          isError: true,
        });
      }
    } catch {
      setFormMessage({
        message: "Password reset error. Please try again.",
        isError: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!isValidToken) {
    return <div>Invalid or expired reset token. Please try again.</div>;
  }

  return (
    <form onSubmit={handlePasswordReset} className="forgot-password-form">
      <h1>Reset Password</h1>
      <div className="forgot-password-form-field">
        <input
          type="email"
          className="forgot-password-form-input"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="forgot-password-form-field position-relative">
        <input
          type={showPassword ? "text" : "password"} // Toggle type based on state
          className="forgot-password-form-input"
          placeholder="New Password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            validatePasswordStrength(e.target.value);
          }}
          required
        />
        <span
          className="forgot-password-password-toggle-icon"
          onClick={() => setShowPassword(!showPassword)} // Toggle visibility state
        >
          {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
        </span>
      </div>
      <ul className="forgot-password-password-criteria">
        {!passwordCriteria.length && (
          <li className={passwordCriteria.length ? "valid" : "invalid"}>
            ✔ Minimum 8 characters
          </li>
        )}
        {!passwordCriteria.lowercase && (
          <li className={passwordCriteria.lowercase ? "valid" : "invalid"}>
            ✔ At least one lowercase letter
          </li>
        )}
        {!passwordCriteria.uppercase && (
          <li className={passwordCriteria.uppercase ? "valid" : "invalid"}>
            ✔ At least one uppercase letter
          </li>
        )}
        {!passwordCriteria.number && (
          <li className={passwordCriteria.number ? "valid" : "invalid"}>
            ✔ At least one number
          </li>
        )}
        {!passwordCriteria.specialChar && (
          <li className={passwordCriteria.specialChar ? "valid" : "invalid"}>
            ✔ At least one special character
          </li>
        )}
      </ul>
      <div className="forgot-password-form-field position-relative">
        <input
          type={showConfirmPassword ? "text" : "password"} // Toggle type for confirm password
          className="forgot-password-form-input"
          placeholder="Confirm New Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        <span
          className="forgot-password-password-toggle-icon"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)} // Toggle visibility state
        >
          {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
        </span>
      </div>
      {formMessage.message && (
        <p
          className={
            formMessage.isError
              ? "forgot-password-error-message"
              : "forgot-password-success-message"
          }
        >
          {formMessage.message}
        </p>
      )}
      <button
        type="submit"
        className="forgot-password-submit-button"
        disabled={loading}
      >
        {loading ? "Resetting..." : "Reset Password"}
      </button>
    </form>
  );
};

const PasswordResetPage: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  return token ? <PasswordReset token={token} /> : <div>Invalid token</div>;
};

export default PasswordResetPage;
